import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchMe, login as apiLogin, logout as apiLogout } from '../services/api';

export interface AuthUser {
  id: number;
  email: string;
  fullName: string;
  prenom: string | null;
  nom: string | null;
  telephone: string | null;
  photoUrl: string | null;
  anonyme: boolean;
  roles: string[];
  rolesAssignes: string[];
  rolePrincipal: string | null;
  bureauRole: { code: string; label: string } | null;
  /**
   * Droits accordés par la grille attachée à la fonction au bureau.
   *
   * Un administrateur les a tous : c'est lui qui tient la grille. Un membre
   * sans fonction garde ce qu'il avait, le champ étant facultatif.
   */
  droits: string[];
  /** Vrai pour les fonctions dirigeantes du bureau, qui accèdent aux modules. */
  accesModules: boolean;
  /** Administrateurs, et au bureau le seul président. */
  peutGererEvenements: boolean;
  /** Super administrateur, ou administrateur président : clôture d'exercice. */
  peutCloturerExercice: boolean;
}

export function useAuth() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['me'],
    queryFn: fetchMe,
    retry: false,
  });

  const user = data?.authenticated ? data.user : null;

  const loginMutation = useMutation({
    mutationFn: ({
      email,
      password,
      code,
      fiabiliserAppareil,
      cle,
    }: {
      email: string;
      password: string;
      // La seconde étape : présents seulement quand le compte est protégé.
      code?: string;
      fiabiliserAppareil?: boolean;
      /**
       * La signature de la clé de sécurité.
       *
       * Elle voyage dans le MÊME appel que le mot de passe, comme le code à six
       * chiffres. Une seconde adresse pour le second facteur supposerait une
       * session ouverte entre les deux — c'est-à-dire exactement ce que le
       * serveur détruit en refusant la première étape.
       */
      cle?: Record<string, string>;
    }) => apiLogin(email, password, code, fiabiliserAppareil, cle),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      try {
        await apiLogout();
      } catch {
        // Serveur injoignable ou session deja expiree : la fermeture
        // cote client se fait quand meme. Rester connecte a l'ecran
        // alors qu'on a demande a sortir serait le pire des deux.
      }
    },
    // onSettled et non onSuccess : le nettoyage suit la demande, pas
    // la reussite de l'appel.
    onSettled: () => {
      queryClient.clear();
      // Rechargement complet vers l'accueil public : rien ne survit —
      // ni cache de requetes, ni etat d'ecran, ni page privee restee a
      // l'affiche derriere une session morte.
      window.location.assign('/');
    },
  });

  return {
    user,
    isLoading,
    /**
     * L'état commercial de l'espace, quand il n'est pas « actif ».
     *
     * Null la plupart du temps, et c'est le cas normal : il n'y a rien à
     * dire d'un espace qui fonctionne. Quand il vaut quelque chose, l'espace
     * est en lecture seule et le bandeau l'annonce — avant la saisie, pas
     * après l'avoir perdue.
     */
    exploitation: (data?.exploitation ?? null) as {
      etat: 'restreint' | 'archive' | 'resilie';
      message: string;
      echeance: string | null;
      joursRestants: number | null;
      offre: string | null;
    } | null,
    /**
     * Le membre connecté a-t-il ce droit ?
     *
     * Passe par la liste calculée par le serveur, jamais par la fonction au
     * bureau : deux règles écrites séparément finissent toujours par diverger,
     * et c'est le serveur qui tranchera l'appel.
     */
    aLeDroit: (droit: string) => user?.droits?.includes(droit) === true,
    login: loginMutation.mutateAsync,
    logout: logoutMutation.mutateAsync,
  };
}
